import { ArrowLeft, ArrowUpRight } from 'lucide-react'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '../components/Button.tsx'
import type { McpServerTile, Me } from '../types.ts'
import { AppMark, itemName, Notice } from '../ui.tsx'

/** An address or command, selectable, with a button that copies it. */
function Copyable({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
    } catch {
      // It's in the box beside the button, to copy by hand.
    }
  }
  return (
    <div className="copyable">
      <input className="mono" aria-label={label} value={value} readOnly onFocus={(e) => e.target.select()} />
      <Button type="button" variant="secondary" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
    </div>
  )
}

/**
 * How to add one of the person's MCP servers to Claude (docs/mcp-servers-plan.md). The
 * address is their company's. The steps follow Claude's own help on custom connectors, and
 * live here alone, so a change to Claude's screens is one edit.
 */
export function McpServer({ me }: { me: Me }) {
  const { appId } = useParams()
  const server = me.current?.mcpServers.find((s) => s.appId === appId)
  return (
    <section className="mcp-setup">
      <Button variant="tertiary" icon={ArrowLeft} to="/settings/mcp-servers">MCP Servers</Button>
      {server ? <Setup server={server} /> : <Notice>You don't have access to that MCP server. Ask your company's administrator.</Notice>}
    </section>
  )
}

function Setup({ server }: { server: McpServerTile }) {
  return (
    <>
      <div className="page-head">
        <AppMark appId={server.appId} name={server.name} kind="mcp" size={56} />
        <div>
          <h1>{itemName({ name: server.name, kind: 'mcp' })}</h1>
          <p className="muted">An MCP server: use it from Claude, signed in as you.</p>
        </div>
      </div>
      {server.setupNote && <Notice kind="info">{server.setupNote}</Notice>}

      {/* Only a company administrator gets adminUrl, so only they see this. It opens in this tab. */}
      {server.adminUrl && (
        <div className="card mcp-admin-card">
          <div>
            <h2>Set up {server.name} for your company</h2>
            <p className="muted">As your company's administrator, set {server.name} up once, on its own site, before your people use it.</p>
          </div>
          <Button href={server.adminUrl} icon={ArrowUpRight}>Set up {server.name}</Button>
        </div>
      )}

      <div className="card form">
        {server.connectUrl && <p className="step-label">Step 1 · Add it to Claude</p>}
        <h2>Its address</h2>
        <Copyable label={`${server.name} address`} value={server.url} />
        <p className="muted small">
          When Claude connects, it sends you here to sign in with your work Microsoft account and to confirm. Only people your
          company has given {server.name} can connect.
        </p>
      </div>

      <div className="card">
        <h2>Claude Pro or Max</h2>
        <ol className="steps">
          <li>In Claude, open <strong>Customize › Connectors</strong> and choose <strong>Add custom connector</strong>.</li>
          <li>Name it <strong>{server.name}</strong> and paste the address above.</li>
          <li>
            If Claude asks how to identify itself, choose <strong>Use Claude's published identity</strong>. Leave the OAuth
            client ID and secret empty.
          </li>
          <li>Choose <strong>Add</strong>, then <strong>Connect</strong>, and sign in.</li>
        </ol>
        <p className="muted small">Claude Desktop and the mobile apps use the connectors you add on claude.ai.</p>
      </div>

      <div className="card">
        <h2>Claude Team or Enterprise</h2>
        <ol className="steps">
          <li>
            An Owner of your Claude organization adds it once, in <strong>Organization settings › Connectors</strong>: choose{' '}
            <strong>Add</strong>, then <strong>Custom</strong>, and paste the address above.
          </li>
          <li>
            Then each person finds <strong>{server.name}</strong> under <strong>Customize › Connectors</strong>, with the{' '}
            <strong>Custom</strong> label, and chooses <strong>Connect</strong>.
          </li>
        </ol>
      </div>

      <div className="card form">
        <h2>Claude Code</h2>
        <p>Run this in a terminal:</p>
        <Copyable label="Command" value={`claude mcp add --transport http ${server.appId} ${server.url}`} />
        <p>
          Then, in Claude Code, run <code>/mcp</code>, choose <strong>{server.appId}</strong> and <strong>Authenticate</strong>.
        </p>
      </div>

      {/* Everyone who has the server gets connectUrl. It opens in this tab. */}
      {server.connectUrl && (
        <div className="card mcp-admin-card">
          <div>
            <p className="step-label">Step 2</p>
            <h2>Connect your {server.name}</h2>
            <p className="muted">Link your own account, on {server.name}'s site, so Claude can use it as you.</p>
          </div>
          <Button href={server.connectUrl} icon={ArrowUpRight}>Connect {server.name}</Button>
        </div>
      )}
    </>
  )
}
